import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { isSchoolAdminUser, isSuperAdminUser } from "@/lib/userRoutes";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";
import { FloatingPanelProvider } from "@/components/floatingPanel";
import { AuthPage } from "@/pages/AuthPage";
import { RouteShellSkeleton } from "@/components/skeleton";
import { AdminShell } from "@/components/admin/AdminShell";
import { getHomeRoute } from "@/lib/roleRouter";
import { runtimeConfig } from "@/lib/runtimeConfig";
import { setConsentTransport } from "@/features/consent/consentLedger";
import { createHttpConsentTransport } from "@/features/consent/httpConsentTransport";
import { RECORD_LIVE_ROUTE, recordLiveVisibleFor } from "@/features/record/recordAccess";
import { UsageBeaconMount } from "@/features/usage/UsageBeaconMount";

const lazyPage = (loader, exportName) =>
  lazy(() => loader().then((mod) => ({ default: mod[exportName] })));

/**
 * SPEED wave 1, fix 5 — LAZY, like the other 58 routes.
 *
 * DashboardPage is 2.1 KB of its own code and was the only page still imported EAGERLY here. Its
 * import GRAPH is what mattered: it pulls the two dashboard components, which reach
 * DomainTrendsChart, which imports recharts -- 241 KB of the entry chunk, plus react-smooth, d3-*
 * and decimal.js-light behind it. Measured by attributing the entry chunk's bytes through its own
 * source map, not guessed from file sizes.
 *
 * A school admin never even sees it: DashboardRoute redirects them to /admin, and they downloaded
 * the chart library anyway.
 *
 * DECLARED BELOW `lazyPage`, NOT WHERE THE OLD `import` STOOD. My first attempt left it among the
 * imports, which put it in `lazyPage`'s temporal dead zone: the bundle still BUILT (bundling does
 * not execute), so the byte win measured correctly while the app would have crashed on load.
 * Three suites caught it.
 */
const DashboardPage = lazyPage(() => import("@/pages/DashboardPage"), "DashboardPage");

// ADMIN-A1 — the principal's shell + its five places (Home real; the other four are PRES-1 placeholders).
const AdminHome = lazyPage(() => import("@/pages/admin/AdminHome"), "default");
const AdminTeam = lazyPage(() => import("@/pages/admin/AdminTeam"), "default");
const AdminLibrary = lazyPage(() => import("@/pages/admin/AdminLibrary"), "default");
// AW-2 — AdminTeacherLessons and AdminTeacherGrowth are no longer routes of their own: the one
// teacher page imports them as its two tab panels, so they travel in ITS chunk. The lesson VIEW
// is still its own route.
const AdminTeacherLessonView = lazyPage(() => import("@/pages/admin/AdminTeacherLessonView"), "default");
const AdminTeacherPage = lazyPage(() => import("@/pages/admin/AdminTeacherPage"), "default");
const AdminSetup = lazyPage(() => import("@/pages/admin/AdminSetup"), "default");
// F14 SETUP-LOOP-1 — the recording schedule gets a door inside the admin shell (C-116 item 6,
// option (i)). The page that SETS a policy was reachable only by typing /school-setup.
const AdminRecordingSchedule = lazyPage(() => import("@/pages/admin/AdminRecordingSchedule"), "default");
// ONBOARD-1 B2 — Access: the org admin approves her own teachers (school_admin only).
const AdminAccess = lazyPage(() => import("@/pages/admin/AdminAccess"), "default");
// ADMIN-D2·S2 — School Growth supersedes School Progress in the rail (R3).
const AdminGrowth = lazyPage(() => import("@/pages/admin/AdminGrowth"), "default");
const AdminReports = lazyPage(() => import("@/pages/admin/AdminReports"), "default");
// W39 (C-359 §2) — the admin's OWN leadership goals, private to them.
const AdminMyLeadership = lazyPage(() => import("@/pages/admin/AdminMyLeadership"), "default");

const MasterAdminPage = lazyPage(() => import("@/pages/MasterAdminPage"), "MasterAdminPage");
const MasterAdminUsersPage = lazyPage(() => import("@/pages/MasterAdminUsersPage"), "MasterAdminUsersPage");
const MasterAdminUserDetailPage = lazyPage(() => import("@/pages/MasterAdminUserDetailPage"), "MasterAdminUserDetailPage");
const MasterAdminOrganizationsPage = lazyPage(() => import("@/pages/MasterAdminOrganizationsPage"), "MasterAdminOrganizationsPage");
const MasterAdminOrganizationDetailPage = lazyPage(() => import("@/pages/MasterAdminOrganizationDetailPage"), "MasterAdminOrganizationDetailPage");
const MasterAdminAuthActivityPage = lazyPage(() => import("@/pages/MasterAdminAuthActivityPage"), "MasterAdminAuthActivityPage");
const MasterAdminAuditPage = lazyPage(() => import("@/pages/MasterAdminAuditPage"), "MasterAdminAuditPage");
const MasterAdminWorkspacesPage = lazyPage(() => import("@/pages/MasterAdminWorkspacesPage"), "MasterAdminWorkspacesPage");
const MasterAdminWorkspaceDetailPage = lazyPage(() => import("@/pages/MasterAdminWorkspaceDetailPage"), "MasterAdminWorkspaceDetailPage");
const MasterAdminVideosPage = lazyPage(() => import("@/pages/MasterAdminVideosPage"), "MasterAdminVideosPage");
const MasterAdminVideoDetailPage = lazyPage(() => import("@/pages/MasterAdminVideoDetailPage"), "MasterAdminVideoDetailPage");
const MasterAdminStoragePage = lazyPage(() => import("@/pages/MasterAdminStoragePage"), "MasterAdminStoragePage");
const MasterAdminDependenciesPage = lazyPage(() => import("@/pages/MasterAdminDependenciesPage"), "MasterAdminDependenciesPage");
const MasterAdminAIQualityPage = lazyPage(() => import("@/pages/MasterAdminAIQualityPage"), "MasterAdminAIQualityPage");
const MasterAdminUsagePage = lazyPage(() => import("@/pages/MasterAdminUsagePage"), "MasterAdminUsagePage");
const MasterAdminIncidentsPage = lazyPage(() => import("@/pages/MasterAdminIncidentsPage"), "MasterAdminIncidentsPage");
const MasterAdminSupportPage = lazyPage(() => import("@/pages/MasterAdminSupportPage"), "MasterAdminSupportPage");
const TeachersPage = lazyPage(() => import("@/pages/TeachersPage"), "TeachersPage");
const VideosPage = lazyPage(() => import("@/pages/VideosPage"), "VideosPage");
const TeacherProfilePage = lazyPage(() => import("@/pages/TeacherProfilePage"), "TeacherProfilePage");
const TeacherLatestLessonPage = lazyPage(() => import("@/pages/TeacherLatestLessonPage"), "TeacherLatestLessonPage");
const TeacherHistoryPage = lazyPage(() => import("@/pages/TeacherHistoryPage"), "TeacherHistoryPage");
const TeacherOperationsPage = lazyPage(() => import("@/pages/TeacherOperationsPage"), "TeacherOperationsPage");
const CoachingHubPage = lazyPage(() => import("@/pages/CoachingHubPage"), "CoachingHubPage");
// URGENT-1 (sweep finding): CohortManagementPage ships a DEFAULT export — loading it by name resolved
// to undefined, breaking /cohorts (training_admin) identically to the settings page. Same one-token fix.
const CohortManagementPage = lazyPage(() => import("@/pages/CohortManagementPage"), "default");
const MasterSchedulePage = lazyPage(() => import("@/pages/MasterSchedulePage"), "MasterSchedulePage");
const ObservationSetupPage = lazyPage(() => import("@/pages/ObservationSetupPage"), "ObservationSetupPage");
const ReportsPage = lazyPage(() => import("@/pages/ReportsPage"), "ReportsPage");
const NotificationsPage = lazyPage(() => import("@/pages/NotificationsPage"), "NotificationsPage");
// URGENT-1: this page ships a DEFAULT export (unlike its named-export siblings), so it must be loaded
// by "default" — loading it by name resolved to undefined → RouteErrorBoundary. Guarded by the route-
// resolution + lazyPage-integrity regression tests so this class of mismatch fails CI going forward.
const CalendarPage = lazyPage(() => import("@/components/calendar/CalendarPage"), "default");
const OnboardingPage = lazyPage(() => import("@/pages/OnboardingPage"), "OnboardingPage");
// S2A-4 — the getting-started GUIDE (how to do things), distinct from /onboarding (whether setup is
// done). Lazy like its neighbours: an admin who never opens it pays nothing for it.
const GettingStartedPage = lazyPage(() => import("@/pages/GettingStartedPage"), "GettingStartedPage");
const ConsentPage = lazyPage(() => import("@/pages/ConsentPage"), "ConsentPage");
const TeacherPrivacyPage = lazyPage(() => import("@/pages/TeacherPrivacyPage"), "TeacherPrivacyPage");
const VideoPlayerPage = lazyPage(() => import("@/pages/VideoPlayerPage"), "VideoPlayerPage");
const VideoRecorderPage = lazyPage(() => import("@/pages/VideoRecorderPage"), "VideoRecorderPage");
const FrameworksPage = lazyPage(() => import("@/pages/FrameworksPage"), "FrameworksPage");
const PrivacyReviewQueuePage = lazyPage(() => import("@/pages/PrivacyReviewQueuePage"), "PrivacyReviewQueuePage");
const RecognitionReviewPage = lazyPage(() => import("@/pages/RecognitionReviewPage"), "RecognitionReviewPage");
const ExemplarLibraryPage = lazyPage(() => import("@/pages/ExemplarLibraryPage"), "ExemplarLibraryPage");
// T3 — four-place shell placeholder pages (replaced wholesale by later build tasks).
const MyGrowthPage = lazyPage(() => import("@/pages/MyGrowthPage"), "MyGrowthPage");
const RecognitionPage = lazyPage(() => import("@/pages/RecognitionPage"), "RecognitionPage");
const TeacherWorkspacePage = lazyPage(() => import("@/pages/TeacherWorkspacePage"), "TeacherWorkspacePage");
const TeacherSelfProfilePage = lazyPage(() => import("@/pages/TeacherSelfProfilePage"), "TeacherSelfProfilePage");
// B6-i: /my-lessons is now the real full lesson archive (was a redirect stub).
const TeacherLessonsPage = lazyPage(() => import("@/pages/TeacherLessonsPage"), "TeacherLessonsPage");
const MyPlansPage = lazyPage(() => import("@/pages/MyPlansPage"), "MyPlansPage");
// B5a: the standalone teacher list-tabs (Lessons / Coaching / Badges) are
// consolidated into the single /my-workspace home. Their routes now REDIRECT
// there instead of mounting a separate page (the page components still exist
// and are rendered directly by their own tests / deep record routes).
const ActionPlanRecordPage = lazyPage(() => import("@/pages/ActionPlanRecordPage"), "ActionPlanRecordPage");
const ReflectionRecordPage = lazyPage(() => import("@/pages/ReflectionRecordPage"), "ReflectionRecordPage");
// T1 — dev-only shared-component visual QA harness (see DesignPreviewPage).
const DesignPreviewPage = lazyPage(() => import("@/pages/DesignPreviewPage"), "DesignPreviewPage");
// NAV-SWEEP-1b (N5) — the branded not-found for any address that does not resolve.
const NotFoundPage = lazyPage(() => import("@/pages/NotFoundPage"), "NotFoundPage");
// RECORD-1 — lazy, so the recorder and its page stay OFF the main bundle: a teacher who never
// records should not pay for MediaRecorder plumbing on every page load.
const RecordPage = lazyPage(() => import("@/pages/RecordPage"), "RecordPage");

function LazyRoute({ children }) {
  return (
    <Suspense fallback={<RouteShellSkeleton />}>
      {children}
    </Suspense>
  );
}

function HomeRedirect() {
  const { user, initializing } = useAuth();

  if (initializing) {
    return null;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <Navigate to={getHomeRoute(user)} replace />;
}

export function RoleAwareAlias({ teacherTo, adminTo }) {
  const { user, initializing } = useAuth();
  if (initializing) return null;
  const role = user?.tenant_role || user?.role;
  return <Navigate to={role === "teacher" ? teacherTo : adminTo} replace />;
}

// CAL-1 — /calendar retirement, the RoleAwareAlias idiom applied to keep admin on-page. TEACHERS:
// the standalone page is retired, so redirect home and auto-open the popup (the calendar IS the
// popup). ADMIN: unchanged — render the full page exactly as before.
export function CalendarRouteDisposition({ children }) {
  const { user, initializing } = useAuth();
  if (initializing) return null;
  const role = user?.tenant_role || user?.role;
  if (role === "teacher") return <Navigate to="/my-workspace?calendar=open" replace />;
  return children;
}

export function RoleAwareCoachingRoute() {
  const { user, initializing } = useAuth();
  if (initializing) return null;
  const role = user?.tenant_role || user?.role;
  if (role === "teacher") {
    // B5a: teachers land on the consolidated workspace, never a retired tab.
    return <Navigate to="/my-workspace" replace />;
  }
  return <LazyRoute><CoachingHubPage /></LazyRoute>;
}

// TROOM-2 (A): the Room's address is /teachers-room. The old /recognition path redirects here,
// PRESERVING the query string so a ?lesson=&t= deep-link (from the lesson page) survives the hop.
// Exported for the route-resolution test.
// ADMIN-B2-ADOPT-1 — the legacy admin landing, routed by role. Exported for the route tests.
export function DashboardRoute() {
  const { user } = useAuth();
  if (isSchoolAdminUser(user)) return <Navigate to="/admin" replace />;
  return (
    <LazyRoute>
      <DashboardPage />
    </LazyRoute>
  );
}

/**
 * AW-2 — the two drills the one teacher page REPLACES keep working as doors. A bookmark, an old
 * link in an email, or an inbound link I failed to find in the sweep lands on the right TAB of the
 * new page instead of a 404. `replace` so Back does not bounce her through the dead route.
 *
 * The lesson VIEW route (/admin/teachers/:teacherId/lessons/:videoId) is deliberately NOT redirected
 * — AW-2 replaces the two index pages, not the lesson a row opens.
 */
export function AdminTeacherTabRedirect({ tab }) {
  const { teacherId } = useParams();
  // `lessons` is the page's default tab, so it is left OUT of the query string rather than written
  // as a redundant ?tab=lessons that the page would immediately drop anyway.
  return <Navigate to={`/admin/teachers/${teacherId}${tab ? `?tab=${tab}` : ""}`} replace />;
}

/**
 * W39 / C-362 §5 — a super admin asking for My leadership goes to the master-admin shell.
 *
 * WHAT THIS DOES AND DOES NOT DO — corrected after a mutant contradicted the first version of this
 * comment, which claimed the role list alone would ADMIT a super admin. It does not.
 *
 * `canAccessTenantRole` does return TRUE for a super admin before it reads the allowed list. But
 * `ProtectedRoute` applies a SECOND gate, `canAccess`, which IS super-admin-aware: with a non-empty
 * `allowedTenantRoles` that omits super_admin it returns `startsWithAny(path, SUPER_ADMIN_ROUTES)`,
 * and `/admin` is not in that list. So `allowedTenantRoles={["school_admin"]}` already keeps a
 * master admin OUT. ⚑ Reading one helper and reporting the guard's behaviour was the error.
 *
 * What it gives them is `RoleMismatchPage` — a dead end. C-362 §5 asks for a REDIRECT to the
 * master-admin shell, which is the NAV-1 lesson: a wrong-shell request goes home rather than
 * landing on a card explaining the refusal. ⚑ SO THIS WRAPPER SUPPLIES THE DESTINATION, NOT THE
 * EXCLUSION, and the route test asserts the destination for exactly that reason — an absence
 * assertion here passes whether the wrapper is present or not.
 *
 * The ruling's own reason is the page's: these are goals for your own leadership practice with
 * teachers you work with, and a master admin has neither. `training_admin` is out of scope per
 * C-362 §5 and never reaches here: absent from the allowed list AND on LayoutShell, not AdminShell.
 */
export function SuperAdminToMasterShell({ children }) {
  const { user } = useAuth();
  if (isSuperAdminUser(user)) return <Navigate to="/master-admin" replace />;
  return children;
}

/**
 * NEW-11 (C-408 §7) — a school administrator asking for a teacher's RECORD lands on that same
 * teacher's ADMIN page, not on a refusal and not on a list.
 *
 * ⚑ SIX DOORS ADMIT HER, NOT ONE. The order names `/teachers/{id}`, but `latest-lesson`,
 * `coaching`, `history`, `action-plan` and `reflections` all carry the same role list, so
 * redirecting the entry alone would leave five bookmarks and five inbound links reaching the page
 * the ruling takes her off. (`/operations` is `superAdminOnly` and is left exactly as it is.)
 *
 * ⚑ THE DESTINATION KEEPS THE TEACHER SHE ASKED FOR. `/admin/teachers/{id}` is school_admin-ONLY
 * and is the same person on the surface built for her role, so she arrives where she was going
 * instead of at the roster having to find the row again. F-73's ruling named the roster for
 * `/observation/new`, which has no teacher in the URL to preserve; this one does, and discarding
 * it would be the same defect in a smaller form. If MA's NEW-11 named a different destination,
 * that ruling wins and this is a one-line change.
 *
 * ⚑ AND SHE LOSES NO DOOR SHE COULD USE. The five sibling records are linked only from
 * `TeacherProfilePage`'s own overflow menu — the page she no longer reaches — so this does not
 * leave her a menu of items that bounce back, which is the defect F-73 refused to create.
 *
 * The redirect sits OUTSIDE `ProtectedRoute` for the reason W39 recorded: that component RETURNS
 * its denial instead of rendering children, so a redirect nested inside it never runs for the
 * role it exists for.
 */
export function SchoolAdminToAdminTeacher({ children }) {
  const { user } = useAuth();
  const { teacherId } = useParams();
  if (isSchoolAdminUser(user)) {
    // No id on these routes should be impossible; the roster is the honest fallback rather than a
    // path built out of an undefined.
    return (
      <Navigate
        to={teacherId ? `/admin/teachers/${encodeURIComponent(teacherId)}` : "/admin/team"}
        replace
      />
    );
  }
  return children;
}

/**
 * F-73 (C-396 §4) — `/observation/new` STAYS for the training admin, whose nav uses it on desktop
 * and on mobile; a SCHOOL admin who lands there goes to her roster.
 *
 * ⚑ SHE WAS ALREADY REFUSED, WHICH IS WHY THIS IS NOT A NO-OP. `ADMIN_ROUTES` has never listed
 * `/observation/new`, so `canAccess` turned her away — to `getHomeRoute`, which is `/admin`. The
 * ruling names the ROSTER, and "blocked" is not "routed": she landed somewhere she had not asked
 * for, one more click from the teachers she was trying to observe.
 *
 * ⚑ IT IS OUTSIDE ProtectedRoute, for the reason W39 wrote down after getting this wrong once:
 * that component evaluates its gates and RETURNS the denial instead of rendering children, so a
 * redirect nested inside it is unreachable for the only role it exists for. It would read correct
 * and do nothing. The route test therefore asserts the DESTINATION, not the page's absence — the
 * absence was already true, for the wrong reason.
 */
export function SchoolAdminToRoster({ children }) {
  const { user } = useAuth();
  if (isSchoolAdminUser(user)) return <Navigate to="/admin/team" replace />;
  return children;
}

export function RecognitionRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/teachers-room${search}`} replace />;
}

// T3: the teacher Room PLACE. Non-teacher behavior is unchanged (admins keep going to the
// recognition-review queue). Exported for the click-crawl test.
export function RecognitionRoute() {
  const { user, initializing } = useAuth();
  if (initializing) return null;
  const role = user?.tenant_role || user?.role;
  if (role === "teacher") {
    return <LazyRoute><RecognitionPage /></LazyRoute>;
  }
  return <Navigate to="/recognition-review" replace />;
}

// T3 (fold ruling G4b): /all-star-library folds INTO Recognition for teachers (content migration
// happens in the Recognition build task). Admin access to the exemplar library is unchanged.
// Exported for the click-crawl test.
export function AllStarLibraryRoute() {
  const { user, initializing } = useAuth();
  if (initializing) return null;
  const role = user?.tenant_role || user?.role;
  if (role === "teacher") {
    return <Navigate to="/teachers-room" replace />;
  }
  return <LazyRoute><ExemplarLibraryPage /></LazyRoute>;
}

// T8 (retirement sweep, deferred from T3): the old-styled teacher /videos recordings list folds
// INTO the redesigned /my-lessons library. Admin access to the videos list is unchanged.
// Exported for the click-crawl test.
export function VideosRoute() {
  const { user, initializing } = useAuth();
  if (initializing) return null;
  const role = user?.tenant_role || user?.role;
  if (role === "teacher") {
    return <Navigate to="/my-lessons" replace />;
  }
  return <LazyRoute><VideosPage /></LazyRoute>;
}

/**
 * CONSENT-LEDGER-1 - INSTALL THE REAL CONSENT TRANSPORT.
 *
 * Until this line existed, activeTransport() lazily built the DEV MOCK in production: a teacher
 * accepted, the client was told ok, the camera opened, and nothing was written anywhere. The
 * consent GATE was intact - she really did accept on screen - but the consent RECORD did not
 * exist, which is what an audit needs and what the owner's standing order requires.
 *
 * At module scope on purpose, so it is in force before any route renders and cannot depend on a
 * component mounting. It must stay in SHIPPED code: a call from a test file is the state this
 * replaces, and backend/tests/test_consent_ledger_1_transport_install.py asserts an installer
 * exists among non-test sources and that its argument is not the mock.
 *
 * Ships with the strict `recorded === true` decision in the same commit, and neither half may land
 * alone: the install without the check lets a 200-that-wrote-nothing open the camera, and the check
 * without the install refuses every camera, because the mock answers for a server that never ran.
 */
setConsentTransport(createHttpConsentTransport());

function AppRoutes() {
  // RECORD-1 rollout (COMMAND 18:35 (2), corrected 19:46): the global flag OR a per-user boolean
  // THE SERVER provides, so the owner can test on his own handset in production while everyone else
  // still sees capture off. No list of people is held in the bundle. Read here rather than at module
  // scope because the decision depends on who is signed in.
  const { user: currentUser } = useAuth();
  // RECORD-ENTRY-1: the SAME helper the buttons call, so a visible door and a registered route
  // cannot disagree. See features/record/recordAccess.js.
  const recordV1Visible = recordLiveVisibleFor(currentUser);

  return (
    <>
      {/* C-322 — the usage beacon's one mount. Renders nothing; it asks the server once per
          session whether recording is allowed, and reports page_view on route change with a
          DECLARED template or no route at all. It sits here because this is the first component
          inside the router that sees every navigation, and because a beacon with no call site
          records nothing — which is how I first shipped it. */}
      <UsageBeaconMount />
      <Routes>
      <Route path="/login" element={<AuthPage />} />
      {/* FIX-2 Item C: signup is directly addressable so the marketing site can deep-link to it;
          AuthPage reads the path and opens in signup mode. */}
      <Route path="/signup" element={<AuthPage />} />

      <Route
        path="/onboarding"
        element={
          <ProtectedRoute allowedTenantRoles={["school_admin", "training_admin"]}>
            <LazyRoute><OnboardingPage /></LazyRoute>
          </ProtectedRoute>
        }
      />
      {/* S2A-4 — the same two roles as /onboarding: this is the guide to the admin surfaces, so it
          admits exactly who can act on them. The page sends the reader home while its copy is
          unapproved, so the route is safe to register before the strings land. */}
      <Route
        path="/getting-started"
        element={
          <ProtectedRoute allowedTenantRoles={["school_admin", "training_admin"]}>
            <LazyRoute><GettingStartedPage /></LazyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/consent"
        element={
          <ProtectedRoute allowedTenantRoles={["teacher"]}>
            <LazyRoute><ConsentPage /></LazyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/privacy"
        element={
          <ProtectedRoute allowedTenantRoles={["teacher", "school_admin", "training_admin", "super_admin"]}>
            <LazyRoute><TeacherPrivacyPage /></LazyRoute>
          </ProtectedRoute>
        }
      />

      {/* ADMIN-A1 — the principal's five places (school_admin only; the DIRECT SWAP). Home is real
          (identity-only); the other four render PRES-1 placeholders until A2–A4. The AdminShell dark
          rail is admin-only chrome — a teacher never reaches these (ProtectedRoute + role gate). */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedTenantRoles={["school_admin"]}>
            <AdminShell><LazyRoute><AdminHome /></LazyRoute></AdminShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/growth"
        element={
          <ProtectedRoute allowedTenantRoles={["school_admin"]}>
            <AdminShell><LazyRoute><AdminGrowth /></LazyRoute></AdminShell>
          </ProtectedRoute>
        }
      />
      {/* W39 (C-359 §2, C-362 §5) — the admin's own leadership goals. School admins only; a super
          admin is redirected to their own shell by the wrapper, which the role list alone does NOT
          do (see SuperAdminToMasterShell). Declared on the server usage route list in the same PR
          under the C-362 §3 grant, per FA-2. */}
      <Route
        path="/admin/my-leadership"
        element={
          /* ⚑ THE REDIRECT IS OUTSIDE ProtectedRoute, AND THE ORDER IS THE WHOLE POINT. Inside it,
             the wrapper is unreachable for the only role it exists for: ProtectedRoute evaluates
             its gates and RETURNS the denial instead of rendering children, so a super admin got
             the role-mismatch card and C-362 §5's redirect never ran. It read correct and did
             nothing. Caught by a route test asserting the DESTINATION rather than the page's
             absence — the absence was already true, for the wrong reason. */
          <SuperAdminToMasterShell>
            <ProtectedRoute allowedTenantRoles={["school_admin"]}>
              <AdminShell><LazyRoute><AdminMyLeadership /></LazyRoute></AdminShell>
            </ProtectedRoute>
          </SuperAdminToMasterShell>
        }
      />
      {/* ADMIN-D2·S2 (R3d) — the superseded address REDIRECTS, never 404s. A principal who
          bookmarked School Progress lands on Growth instead of the not-found page: the NAV-1
          lesson, applied before someone had to find it the hard way. */}
      <Route path="/admin/progress" element={<Navigate to="/admin/growth" replace />} />
      <Route
        path="/admin/team"
        element={
          <ProtectedRoute allowedTenantRoles={["school_admin"]}>
            <AdminShell><LazyRoute><AdminTeam /></LazyRoute></AdminShell>
          </ProtectedRoute>
        }
      />
      {/* F-63 (C-371) / F-25 — school reports. Admin-only, like every other /admin route, and
          declared on BOTH usage route lists in this commit per FA-2 — which is two files, not
          one, as W39 found the hard way. */}
      <Route
        path="/admin/reports"
        element={
          <ProtectedRoute allowedTenantRoles={["school_admin"]}>
            <AdminShell><LazyRoute><AdminReports /></LazyRoute></AdminShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/library"
        element={
          <ProtectedRoute allowedTenantRoles={["school_admin"]}>
            <AdminShell><LazyRoute><AdminLibrary /></LazyRoute></AdminShell>
          </ProtectedRoute>
        }
      />
      {/* ADMIN-A5a — the teacher drill-down (read-only, D2-scoped). Session-derived permission; the
          :teacherId / :videoId params are lookup keys, never a trust boundary (server-enforced). */}
      {/* AW-2 (C-212 §6, ordered C-223 §6) — ONE page per teacher, tabs in the query string. */}
      <Route
        path="/admin/teachers/:teacherId"
        element={
          <ProtectedRoute allowedTenantRoles={["school_admin"]}>
            <AdminShell><LazyRoute><AdminTeacherPage /></LazyRoute></AdminShell>
          </ProtectedRoute>
        }
      />
      {/* AW-2 — was the standalone lessons drill; now a door onto the teacher page's Lessons tab.
          The role gate stays so an unauthorised caller is refused here rather than bounced onward. */}
      <Route
        path="/admin/teachers/:teacherId/lessons"
        element={
          <ProtectedRoute allowedTenantRoles={["school_admin"]}>
            <AdminTeacherTabRedirect />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/teachers/:teacherId/lessons/:videoId"
        element={
          <ProtectedRoute allowedTenantRoles={["school_admin"]}>
            <AdminShell><LazyRoute><AdminTeacherLessonView /></LazyRoute></AdminShell>
          </ProtectedRoute>
        }
      />
      {/* ADMIN-A5b — the growth drill (read-only, shared-set-only recompute; viewer door closed). */}
      {/* AW-2 — was the standalone growth drill; now a door onto the Growth tab. */}
      <Route
        path="/admin/teachers/:teacherId/growth"
        element={
          <ProtectedRoute allowedTenantRoles={["school_admin"]}>
            <AdminTeacherTabRedirect tab="growth" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/schedule"
        element={
          <ProtectedRoute allowedTenantRoles={["school_admin", "training_admin"]}>
            <AdminShell><LazyRoute><AdminRecordingSchedule /></LazyRoute></AdminShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/setup"
        element={
          <ProtectedRoute allowedTenantRoles={["school_admin"]}>
            <AdminShell><LazyRoute><AdminSetup /></LazyRoute></AdminShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/access"
        element={
          <ProtectedRoute allowedTenantRoles={["school_admin"]}>
            <AdminShell><LazyRoute><AdminAccess /></LazyRoute></AdminShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/master-admin"
        element={
          <ProtectedRoute superAdminOnly>
            <LazyRoute><MasterAdminPage /></LazyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/master-admin/users"
        element={
          <ProtectedRoute superAdminOnly>
            <LazyRoute><MasterAdminUsersPage /></LazyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/master-admin/users/:userId"
        element={
          <ProtectedRoute superAdminOnly>
            <LazyRoute><MasterAdminUserDetailPage /></LazyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/master-admin/organizations"
        element={
          <ProtectedRoute superAdminOnly>
            <LazyRoute><MasterAdminOrganizationsPage /></LazyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/master-admin/organizations/:organizationId"
        element={
          <ProtectedRoute superAdminOnly>
            <LazyRoute><MasterAdminOrganizationDetailPage /></LazyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/master-admin/workspaces"
        element={
          <ProtectedRoute superAdminOnly>
            <LazyRoute><MasterAdminWorkspacesPage /></LazyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/master-admin/workspaces/:ownerUserId"
        element={
          <ProtectedRoute superAdminOnly>
            <LazyRoute><MasterAdminWorkspaceDetailPage /></LazyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/master-admin/videos"
        element={
          <ProtectedRoute superAdminOnly>
            <LazyRoute><MasterAdminVideosPage /></LazyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/master-admin/videos/:videoId"
        element={
          <ProtectedRoute superAdminOnly>
            <LazyRoute><MasterAdminVideoDetailPage /></LazyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/master-admin/storage"
        element={
          <ProtectedRoute superAdminOnly>
            <LazyRoute><MasterAdminStoragePage /></LazyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/master-admin/dependencies"
        element={
          <ProtectedRoute superAdminOnly>
            <LazyRoute><MasterAdminDependenciesPage /></LazyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/master-admin/usage"
        element={
          <ProtectedRoute superAdminOnly>
            <LazyRoute><MasterAdminUsagePage /></LazyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/master-admin/ai-quality"
        element={
          <ProtectedRoute superAdminOnly>
            <LazyRoute><MasterAdminAIQualityPage /></LazyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/master-admin/incidents"
        element={
          <ProtectedRoute superAdminOnly>
            <LazyRoute><MasterAdminIncidentsPage /></LazyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/master-admin/support"
        element={
          <ProtectedRoute superAdminOnly>
            <LazyRoute><MasterAdminSupportPage /></LazyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/master-admin/auth-activity"
        element={
          <ProtectedRoute superAdminOnly>
            <LazyRoute><MasterAdminAuthActivityPage /></LazyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/master-admin/audit"
        element={
          <ProtectedRoute superAdminOnly>
            <LazyRoute><MasterAdminAuditPage /></LazyRoute>
          </ProtectedRoute>
        }
      />

      {/* ADMIN-B2-ADOPT-1 — /dashboard is the LEGACY admin landing. The school_admin's world is the
          /admin rail, so she is redirected there rather than dropped into a second, differently-
          chromed product; the redirect (never a 404) keeps an old bookmark working, the D2·S2 idiom.
          ROLE-CONDITIONAL BY CONSTRUCTION: training_admin is out of this arc's scope and still lands
          on its own dashboard, byte-untouched — asserted by test, because a blanket redirect here
          would have silently removed another persona's home. */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute allowedTenantRoles={["school_admin", "training_admin"]}>
            <DashboardRoute />
          </ProtectedRoute>
        }
      />
      <Route
        path="/teachers"
        element={
          <ProtectedRoute allowedTenantRoles={["school_admin", "training_admin"]}>
            <LazyRoute><TeachersPage /></LazyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cohorts"
        element={
          <ProtectedRoute allowedTenantRoles={["training_admin"]}>
            <LazyRoute><CohortManagementPage /></LazyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/teachers/:teacherId"
        element={
          <SchoolAdminToAdminTeacher>
            <ProtectedRoute allowedTenantRoles={["training_admin"]}>
              <LazyRoute><TeacherProfilePage /></LazyRoute>
            </ProtectedRoute>
          </SchoolAdminToAdminTeacher>
        }
      />
      <Route
        path="/teachers/:teacherId/latest-lesson"
        element={
          <SchoolAdminToAdminTeacher>
            <ProtectedRoute allowedTenantRoles={["training_admin"]}>
              <LazyRoute><TeacherLatestLessonPage /></LazyRoute>
            </ProtectedRoute>
          </SchoolAdminToAdminTeacher>
        }
      />
      <Route
        path="/coaching"
        element={
          <ProtectedRoute allowedTenantRoles={["teacher", "school_admin", "training_admin"]}>
            <RoleAwareCoachingRoute />
          </ProtectedRoute>
        }
      />
      <Route
        path="/teachers/:teacherId/coaching"
        element={
          <SchoolAdminToAdminTeacher>
            <ProtectedRoute allowedTenantRoles={["training_admin"]}>
              <LazyRoute><CoachingHubPage /></LazyRoute>
            </ProtectedRoute>
          </SchoolAdminToAdminTeacher>
        }
      />
      <Route
        path="/teachers/:teacherId/history"
        element={
          <SchoolAdminToAdminTeacher>
            <ProtectedRoute allowedTenantRoles={["training_admin"]}>
              <LazyRoute><TeacherHistoryPage /></LazyRoute>
            </ProtectedRoute>
          </SchoolAdminToAdminTeacher>
        }
      />
      <Route
        path="/teachers/:teacherId/operations"
        element={
          <ProtectedRoute superAdminOnly>
            <LazyRoute><TeacherOperationsPage /></LazyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/teachers/:teacherId/action-plan"
        element={
          <SchoolAdminToAdminTeacher>
            <ProtectedRoute allowedTenantRoles={["training_admin"]}>
              <LazyRoute><ActionPlanRecordPage /></LazyRoute>
            </ProtectedRoute>
          </SchoolAdminToAdminTeacher>
        }
      />
      <Route
        path="/teachers/:teacherId/reflections"
        element={
          <SchoolAdminToAdminTeacher>
            <ProtectedRoute allowedTenantRoles={["training_admin"]}>
              <LazyRoute><ReflectionRecordPage /></LazyRoute>
            </ProtectedRoute>
          </SchoolAdminToAdminTeacher>
        }
      />

      <Route
        path="/my-workspace"
        element={
          <ProtectedRoute allowedTenantRoles={["teacher"]}>
            <LazyRoute><TeacherWorkspacePage /></LazyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-badges"
        element={
          <ProtectedRoute allowedTenantRoles={["teacher"]}>
            <Navigate to="/my-workspace" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-profile"
        element={
          <ProtectedRoute allowedTenantRoles={["teacher"]}>
            <LazyRoute><TeacherSelfProfilePage /></LazyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-lessons"
        element={
          <ProtectedRoute allowedTenantRoles={["teacher"]}>
            <LazyRoute><TeacherLessonsPage /></LazyRoute>
          </ProtectedRoute>
        }
      />
      {/* PLAN-R1 — planning's designed home. A functional non-place (NOT a nav pill; R4 stands);
          reached from the week-strip popover + the avatar menu. */}
      <Route
        path="/my-plans"
        element={
          <ProtectedRoute allowedTenantRoles={["teacher"]}>
            <LazyRoute><MyPlansPage /></LazyRoute>
          </ProtectedRoute>
        }
      />
      {/* T3 — the third of the four teacher places. Placeholder page for now. */}
      <Route
        path="/my-growth"
        element={
          <ProtectedRoute allowedTenantRoles={["teacher"]}>
            <LazyRoute><MyGrowthPage /></LazyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-coaching"
        element={
          <ProtectedRoute allowedTenantRoles={["teacher"]}>
            <Navigate to="/my-workspace" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-workspace/goals"
        element={
          <ProtectedRoute allowedTenantRoles={["teacher"]}>
            <LazyRoute><ActionPlanRecordPage /></LazyRoute>
          </ProtectedRoute>
        }
      />
      {/* Fix A: /my-workspace/coaching is no longer a param-dropping redirect. It falls through to
          /my-workspace/:section below, which renders the workspace and scrolls to its coaching
          (Act) zone — teacher coaching is consolidated into the workspace, not a separate hub. */}
      <Route
        path="/my-workspace/reflections"
        element={
          <ProtectedRoute allowedTenantRoles={["teacher"]}>
            <LazyRoute><ReflectionRecordPage /></LazyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-workspace/:section"
        element={
          <ProtectedRoute allowedTenantRoles={["teacher"]}>
            <LazyRoute><TeacherWorkspacePage /></LazyRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/videos"
        element={
          <ProtectedRoute allowedTenantRoles={["teacher", "school_admin", "training_admin"]}>
            <VideosRoute />
          </ProtectedRoute>
        }
      />
      <Route
        path="/teacher/profile"
        element={
          <ProtectedRoute allowedTenantRoles={["teacher"]}>
            <Navigate to="/my-profile" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/teacher/lessons"
        element={
          <ProtectedRoute allowedTenantRoles={["teacher"]}>
            <Navigate to="/my-lessons" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lessons"
        element={
          <ProtectedRoute allowedTenantRoles={["teacher", "school_admin", "training_admin"]}>
            <RoleAwareAlias teacherTo="/my-workspace" adminTo="/videos" />
          </ProtectedRoute>
        }
      />
      <Route
        path="/teachers-room"
        element={
          <ProtectedRoute allowedTenantRoles={["teacher", "school_admin", "training_admin"]}>
            <RecognitionRoute />
          </ProtectedRoute>
        }
      />
      {/* TROOM-2 (A): the old address redirects to the Room, query string preserved (deep-links live). */}
      <Route path="/recognition" element={<RecognitionRedirect />} />
      <Route
        path="/videos/:videoId"
        element={
          <ProtectedRoute allowedTenantRoles={["teacher", "school_admin", "training_admin"]}>
            <LazyRoute><VideoPlayerPage /></LazyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/privacy-review"
        element={
          <ProtectedRoute allowedTenantRoles={["school_admin"]}>
            <LazyRoute><PrivacyReviewQueuePage /></LazyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/recognition-review"
        element={
          <ProtectedRoute allowedTenantRoles={["school_admin"]}>
            <LazyRoute><RecognitionReviewPage /></LazyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute allowedTenantRoles={["school_admin", "training_admin"]}>
            <LazyRoute><ReportsPage /></LazyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute allowedTenantRoles={["teacher", "school_admin", "training_admin", "super_admin"]}>
            <LazyRoute><NotificationsPage /></LazyRoute>
          </ProtectedRoute>
        }
      />
      {/* NOTIF-1 — the two notification surfaces fused into /notifications (inbox leads, prefs is a tab).
          The old preferences URL redirects to the Preferences tab; ?tab= survives a hard refresh. */}
      <Route
        path="/settings/notifications"
        element={<Navigate to="/notifications?tab=preferences" replace />}
      />
      {/* CAL-1 — /calendar retired teacher-side: teachers redirect home + auto-open the popup; admins
          keep the full page (untouched) via CalendarRouteDisposition. */}
      <Route
        path="/calendar"
        element={
          <ProtectedRoute allowedTenantRoles={["teacher", "school_admin", "training_admin", "super_admin"]}>
            <CalendarRouteDisposition>
              <LazyRoute>
                <div className="mx-auto h-[85vh] max-w-4xl overflow-hidden rounded-2xl border border-slate-200 bg-white p-0 shadow-sm sm:my-6">
                  <CalendarPage />
                </div>
              </LazyRoute>
            </CalendarRouteDisposition>
          </ProtectedRoute>
        }
      />
      <Route
        path="/all-star-library"
        element={
          <ProtectedRoute allowedTenantRoles={["teacher", "school_admin", "training_admin"]}>
            <AllStarLibraryRoute />
          </ProtectedRoute>
        }
      />
      <Route
        path="/school-setup"
        element={
          <ProtectedRoute allowedTenantRoles={["school_admin", "training_admin"]}>
            <LazyRoute><FrameworksPage /></LazyRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings"
        element={
          <ProtectedRoute allowedTenantRoles={["school_admin", "training_admin"]}>
            <Navigate to="/settings" replace />
          </ProtectedRoute>
        }
      />
      <Route path="/frameworks" element={<HomeRedirect />} />
      <Route
        path="/master-schedule"
        element={
          <ProtectedRoute allowedTenantRoles={["school_admin", "training_admin"]}>
            <LazyRoute><MasterSchedulePage /></LazyRoute>
          </ProtectedRoute>
        }
      />
      {/* F-73 (C-396 §4) — the route STAYS, because the training admin's nav uses it on desktop and
          on mobile and deleting it would leave two menu items leading nowhere. That is the defect
          MA refused NEW-11 over, and it applies here unchanged.
          ⚑ The role list is narrowed to the one role that may use it, so the DECLARATION now says
          what the ruling says. It previously admitted `school_admin` as well, which read as
          permission while `canAccess` quietly refused her — a declaration and a gate disagreeing is
          how the next reader concludes the wrong thing. */}
      <Route
        path="/observation/new"
        element={
          <SchoolAdminToRoster>
            <ProtectedRoute allowedTenantRoles={["training_admin"]}>
              <LazyRoute><ObservationSetupPage /></LazyRoute>
            </ProtectedRoute>
          </SchoolAdminToRoster>
        }
      />
      <Route
        path="/record"
        element={
          <ProtectedRoute allowedTenantRoles={["teacher", "school_admin", "training_admin"]}>
            <LazyRoute><VideoRecorderPage /></LazyRoute>
          </ProtectedRoute>
        }
      />

      {/* T1 Step 2C — dev-only design preview. In non-production builds it renders openly;
          in production it is gated to super-admins (the existing ProtectedRoute pattern). */}
      <Route
        path="/design-preview"
        element={
          process.env.NODE_ENV !== "production" ? (
            <LazyRoute><DesignPreviewPage /></LazyRoute>
          ) : (
            <ProtectedRoute superAdminOnly>
              <LazyRoute><DesignPreviewPage /></LazyRoute>
            </ProtectedRoute>
          )
        }
      />

      <Route path="/" element={<HomeRedirect />} />
      {/* NAV-SWEEP-1b (N5) — an unresolvable address used to fall through to HomeRedirect, silently
          teleporting the visitor to their dashboard. It now says so, and offers a way back and a way
          home. HomeRedirect keeps "/" (a real address that resolves per role). */}
      {/* RECORD-1 — in-app capture, teacher-only, behind the SERVER's per-user field
          (record_live_enabled, read via recordLiveVisibleFor). When it says no the route is NOT
          REGISTERED AT ALL, so the path falls through to the catch-all and 404s — it cannot be
          reached by guessing the URL.

          RECORD-FLAG-ONE-1 removed the client build flag that used to sit beside that field as an
          OR. Two switches that could disagree, and the global one granted recording to every
          teacher served the build at once, which is what the per-user field existed to avoid.

          MOUNTED AT /record/live, NOT /record. The goal asked for /record; that path is ALREADY a
          shipped surface — the choose-a-file page above, open to teachers and both admin roles.
          Registering a second /record would have been dead code, because React Router takes the
          first match and this one is declared later. Taking the path over would have removed the
          upload surface. So capture gets its own path beside it and the collision is reported. */}
      {recordV1Visible ? (
        <Route
          path={RECORD_LIVE_ROUTE}
          element={
            <ProtectedRoute allowedTenantRoles={["teacher"]}>
              <LazyRoute><RecordPage /></LazyRoute>
            </ProtectedRoute>
          }
        />
      ) : null}
      <Route path="*" element={<LazyRoute><NotFoundPage /></LazyRoute>} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <RouteErrorBoundary>
        {/* MON-2: the floating-panel open-state lives ABOVE the routes so a page body (the Home Mira
            rail) and the launchers rendered inside its LayoutShell share ONE context. */}
        <FloatingPanelProvider>
          <AppRoutes />
        </FloatingPanelProvider>
      </RouteErrorBoundary>
    </AuthProvider>
  );
}

export default App;
